import React, {useEffect, useState} from 'react';
import styles from "./ReportTable.module.css"
import {reportFieldLabels} from "../../../types/report.consts";
import {FieldChangeParams, ReportData} from "../../../types/report.types";
import {LoadingErrorBlock} from "../../shared/LoadingErrorBlock";
import {ClearFieldModal} from "../ClearFieldModal/ClearFieldModal";

interface ReportTableProps {
    isAdmin: boolean;
    reportData?: ReportData;
    onDataChange?: (data: ReportData) => void;
    onClearField?: (section: 'resultPO' | 'resultPOChildren', key: string, fieldLabel: string) => void;
    isLoading: boolean;
    isError: boolean;
    selectedYear?: number;
    selectedWeek?: number;
}

function calculateWeeklyInspectionPlan(
    totalYearPlan?: number,
    inspectedCount?: number,
    remainingWeeks?: number
): number {
    if (
        typeof totalYearPlan !== 'number' ||
        typeof inspectedCount !== 'number' ||
        typeof remainingWeeks !== 'number' ||
        isNaN(totalYearPlan) ||
        isNaN(inspectedCount) ||
        isNaN(remainingWeeks) ||
        remainingWeeks <= 0
    ) return 0;
    if (inspectedCount >= totalYearPlan) return 0;

    const remainingInspections = totalYearPlan - inspectedCount;
    return Math.ceil(remainingInspections / remainingWeeks);
}

export const ReportTable: React.FC<ReportTableProps> = ({
                                                            isAdmin,
                                                            reportData,
                                                            onDataChange,
                                                            onClearField,
                                                            isLoading,
                                                            isError,
                                                            selectedYear,
    selectedWeek
                                                        }) => {
    const preventWheelChange = (e: React.WheelEvent<HTMLInputElement>) => {
        e.preventDefault();
        e.currentTarget.blur();
    };

    const [data, setData] = useState<ReportData>(
        reportData || {
            resultPO: Object.keys(reportFieldLabels.resultPO).reduce((acc, key) => {
                acc[key] = {weekly: null, total: null};
                return acc;
            }, {} as Record<string, { weekly: null, total: null }>),
            resultPOChildren: Object.keys(reportFieldLabels.resultPOChildren).reduce((acc, key) => {
                acc[key] = {weekly: null, total: null, totalWithPrevYear: null};
                return acc;
            }, {} as Record<string, { weekly: null, total: null, totalWithPrevYear: null }>)
        }
    );

    const [showClearModal, setShowClearModal] = useState(false);
    const [fieldToClear, setFieldToClear] = useState<{
        section: 'resultPO' | 'resultPOChildren';
        key: string;
        label: string;
    } | null>(null);
    const [hoveredField, setHoveredField] = useState<string | null>(null);

    const handleFieldChange = ({section, key, field, value}: FieldChangeParams) => {
        const newData = {...data} as ReportData;
        if (!newData[section][key]) {
            if (section === 'resultPO') {
                newData.resultPO[key] = {weekly: null, total: null};
            } else {
                newData.resultPOChildren[key] = {weekly: null, total: null, totalWithPrevYear: null};
            }
        }

        if (section === 'resultPO') {
            newData.resultPO[key][field] = value;
        } else {
            newData.resultPOChildren[key][field] = value;
        }
        setData(newData);
        if (onDataChange) {
            onDataChange(newData);
        }
    };

    const handleClearClick = (section: 'resultPO' | 'resultPOChildren', key: string, label: string) => {
        setFieldToClear({ section, key, label });
        setShowClearModal(true);
    };

    const handleConfirmClear = () => {
        if (fieldToClear && onClearField) {
            onClearField(fieldToClear.section, fieldToClear.key, fieldToClear.label);
        }
        setShowClearModal(false);
        setFieldToClear(null);
    };

    const handleCloseClearModal = () => {
        setShowClearModal(false);
        setFieldToClear(null);
    };

    useEffect(() => {
        if (reportData) {
            setData(reportData);
        }
    }, [reportData]);

    return (
        <div className={styles.tableWrapper}>
            <LoadingErrorBlock
                isLoading={isLoading}
                isError={isError}
                errorMessage={"Ошибка загрузки данных отчетов. Пожалуйста, попробуйте позже."}
            />

            <div className={`${styles.table} ${styles.tableOP}`}>
                <div className={styles.tableHeaderPO}>
                    <p className={styles.tableHeaderCol}>
                            По результатам ПО в {new Date().getFullYear()} году
                    </p>
                    <div className={styles.tableHeaderCol}>
                        <input
                            type="number"
                            onWheel={preventWheelChange}
                            min={0}
                            title={"Недельный план осмотра"}
                            disabled={true}
                            value={(() => {
                                const totalYearPlan = typeof data.annualInspectionPlan === 'number' ? data.annualInspectionPlan : 0;
                                const examinedTotal = typeof data?.resultPO?.examinedTotal?.total === 'number' ? data.resultPO.examinedTotal.total : 0;
                                let remainingWeeks = 0;
                                if (selectedWeek && typeof selectedWeek === 'object' && typeof (selectedWeek as any).week === 'number') {
                                    remainingWeeks = 52 - (selectedWeek as any).week + 1;
                                } else if (typeof selectedWeek === 'number') {
                                    remainingWeeks = 52 - selectedWeek + 1;
                                }
                                const weeklyPlan = calculateWeeklyInspectionPlan(totalYearPlan, examinedTotal, remainingWeeks);
                                return weeklyPlan || '';
                            })()}
                        />
                    </div>
                    <div className={styles.tableHeaderCol}>
                        <input
                            type="number"
                            onWheel={preventWheelChange}
                            min={0}
                            title={"Годовой план осмотра"}
                            value={data.annualInspectionPlan || ''}
                            onChange={(e) => {
                                const value = e.target.value === '' ? null : Number(e.target.value);
                                if (onDataChange) {
                                    onDataChange({
                                        ...data,
                                        annualInspectionPlan: value
                                    })
                                }
                            }}
                        />
                    </div>
                </div>
                <div className={styles.tableBody}>
                    <div className={styles.rowHead}>
                        <div className={styles.col}>Показатель</div>
                        <div className={styles.col}>За отчетную неделю</div>
                        <div className={styles.col}>Нарастающим итогом, с начала года</div>
                    </div>
                    {Object.entries(reportFieldLabels.resultPO)
                        .map(([key, label]) => (
                            <div key={key} className={styles.row}>
                                <div className={styles.col}>{label}</div>
                                <div className={styles.col}>
                                    <div 
                                        className={styles.inputWrapper}
                                        onMouseEnter={() => setHoveredField(`resultPO-${key}`)}
                                        onMouseLeave={() => setHoveredField(null)}
                                    >
                                        <input
                                            type="number"
                                            onWheel={preventWheelChange}
                                            value={data.resultPO[key]?.weekly || ''}
                                            min={0}
                                            onChange={(e) => {
                                                const value = e.target.value === '' ? null : Math.max(0, e.target.valueAsNumber || 0);
                                                handleFieldChange({
                                                    section: 'resultPO',
                                                    key,
                                                    field: 'weekly',
                                                    value
                                                });
                                            }}
                                        />
                                        {hoveredField === `resultPO-${key}` && (data.resultPO[key]?.weekly !== null && data.resultPO[key]?.weekly !== undefined && data.resultPO[key]?.weekly !== 0) && (
                                            <button
                                                type="button"
                                                className={styles.clearButton}
                                                onClick={() => handleClearClick('resultPO', key, label)}
                                                title="Очистить поле"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className={styles.col}>
                                    <input
                                        onWheel={preventWheelChange}
                                        disabled={!isAdmin}
                                        type="number"
                                        value={data.resultPO[key]?.total || ''}
                                        onChange={(e) => {
                                            const value = e.target.value === '' ? null : Number(e.target.value);
                                            if (value === null) {
                                                // Если очистили total — ничего не делаем или сбрасываем weekly по желанию
                                                return;
                                            }
                                            const prevTotal = data.resultPO[key]?.total || 0;
                                            const prevWeekly = data.resultPO[key]?.weekly || 0;
                                            const delta = value - prevTotal;
                                            // Сначала обновляем weekly
                                            handleFieldChange({
                                                section: 'resultPO',
                                                key,
                                                field: 'weekly',
                                                value: prevWeekly + delta
                                            });
                                            // Затем обновляем total для UI (но не для сохранения в БД)
                                            handleFieldChange({
                                                section: 'resultPO',
                                                key,
                                                field: 'total',
                                                value
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                </div>
            </div>
            <div className={`${styles.table} ${styles.tableOPChildren}`}>
                <div className={styles.tableHeader}>Показатели по результатам профилактического осмотра детей</div>
                <div className={styles.tableBody}>
                    <div className={styles.rowHead}>
                        <div className={styles.col}>Показатель</div>
                        <div className={styles.col}>За отчетную неделю</div>
                        <div className={styles.col}>Нарастающим итогом, с начала года</div>
                        <div className={styles.col}>Суммарно с
                            данными {!selectedYear ? new Date().getFullYear() - 1 : selectedYear - 1}</div>
                    </div>
                    {Object.entries(reportFieldLabels.resultPOChildren).map(([key, label]) => (
                        <div key={key} className={styles.row}>
                            <div className={styles.col}>{label}</div>
                            <div className={styles.col}>
                                <div 
                                    className={styles.inputWrapper}
                                    onMouseEnter={() => setHoveredField(`resultPOChildren-${key}`)}
                                    onMouseLeave={() => setHoveredField(null)}
                                >
                                    <input
                                        type="number"
                                        onWheel={preventWheelChange}
                                        value={data.resultPOChildren[key]?.weekly || ''}
                                        onChange={(e) => {
                                            const value = e.target.value === '' ? null : Math.max(0, e.target.valueAsNumber || 0);
                                            handleFieldChange({
                                                section: 'resultPOChildren',
                                                key,
                                                field: 'weekly',
                                                value
                                            });
                                        }}
                                    />
                                    {hoveredField === `resultPOChildren-${key}` && (data.resultPOChildren[key]?.weekly !== null && data.resultPOChildren[key]?.weekly !== undefined && data.resultPOChildren[key]?.weekly !== 0) && (
                                        <button
                                            type="button"
                                            className={styles.clearButton}
                                            onClick={() => handleClearClick('resultPOChildren', key, label)}
                                            title="Очистить поле"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className={styles.col}>
                                <input
                                    type="number"
                                    onWheel={preventWheelChange}
                                    disabled={!isAdmin}
                                    value={data.resultPOChildren[key]?.total || ''}
                                    onChange={(e) => {
                                        const value = e.target.value === '' ? null : Number(e.target.value);
                                        if (value === null) {
                                            // Если очистили total — ничего не делаем или сбрасываем weekly по желанию
                                            return;
                                        }
                                        const prevTotal = data.resultPOChildren[key]?.total || 0;
                                        const prevWeekly = data.resultPOChildren[key]?.weekly || 0;
                                        const delta = value - prevTotal;
                                        // Сначала обновляем weekly
                                        handleFieldChange({
                                            section: 'resultPOChildren',
                                            key,
                                            field: 'weekly',
                                            value: prevWeekly + delta
                                        });
                                        // Затем обновляем total для UI (но не для сохранения в БД)
                                        handleFieldChange({
                                            section: 'resultPOChildren',
                                            key,
                                            field: 'total',
                                            value
                                        });
                                    }}
                                />
                            </div>
                            <div className={styles.col}>
                                <input
                                    type="number"
                                    onWheel={preventWheelChange}
                                    disabled
                                    value={data.resultPOChildren[key]?.totalWithPrevYear || ''}
                                    onChange={(e) => {
                                        const value = e.target.value === '' ? null : Number(e.target.value);
                                        handleFieldChange({
                                            section: 'resultPOChildren',
                                            key,
                                            field: 'totalWithPrevYear',
                                            value
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <ClearFieldModal
                show={showClearModal}
                onClose={handleCloseClearModal}
                onConfirm={handleConfirmClear}
                fieldLabel={fieldToClear?.label || ''}
            />
        </div>
    )
};